import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SCHEDULE_TASK_TYPES,
  datetimeLocalToNaive,
  toDatetimeLocalValue,
  type ScheduleTaskType,
} from "@/domain/schedule";
import { asScheduleSlotId } from "@/types/identifiers";
import type { ScheduleSlot, ScheduleTeamEntry } from "@/api/hooks/use-schedule";
import type { ScheduleActions } from "@/hooks/use-schedule-actions";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teams: ScheduleTeamEntry[];
  /** The slot being edited, or null to create a new one. */
  slot: ScheduleSlot | null;
  /** Boat context (event_build_id). Null in header "Add" so the user picks a boat. */
  buildId: string | null;
  /** Prefill the time (ISO) — used when creating from a ghost slot on the planner. */
  presetStart?: string | null;
  actions: ScheduleActions;
}

export function ScheduleSlotDialog({
  open,
  onOpenChange,
  teams,
  slot,
  buildId,
  presetStart,
  actions,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {open && (
          <SlotForm
            key={slot?.id ?? `${buildId ?? "any"}-${presetStart ?? ""}`}
            teams={teams}
            slot={slot}
            buildId={buildId}
            presetStart={presetStart}
            actions={actions}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SlotForm({
  teams,
  slot,
  buildId,
  presetStart,
  actions,
  onDone,
}: {
  teams: ScheduleTeamEntry[];
  slot: ScheduleSlot | null;
  buildId: string | null;
  presetStart?: string | null;
  actions: ScheduleActions;
  onDone: () => void;
}) {
  const isEdit = slot !== null;
  const [taskType, setTaskType] = useState<ScheduleTaskType>(slot?.task_type ?? "checks");
  const [buildSel, setBuildSel] = useState<string>(slot?.event_build_id ?? buildId ?? "");
  const [when, setWhen] = useState<string>(toDatetimeLocalValue(slot?.scheduled_start ?? presetStart));

  const needsBoatPicker = !isEdit && !buildId;
  const canSave = !!buildSel && !actions.isSaving;

  async function save() {
    const scheduled_start = datetimeLocalToNaive(when);
    try {
      if (isEdit && slot) {
        await actions.handleUpdate(asScheduleSlotId(slot.id), { task_type: taskType, scheduled_start });
      } else {
        await actions.handleCreate({ event_build_id: buildSel, task_type: taskType, scheduled_start });
      }
      onDone();
    } catch {
      // Error toasted by the action; keep the dialog open for retry.
    }
  }

  async function remove() {
    if (!slot) return;
    try {
      await actions.handleDelete(asScheduleSlotId(slot.id));
      onDone();
    } catch {
      // keep open
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit slot" : "Schedule work"}</DialogTitle>
        <DialogDescription>Status updates automatically as the work is signed off.</DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        {needsBoatPicker && (
          <div className="space-y-1.5">
            <Label>Boat</Label>
            <Select value={buildSel} onValueChange={setBuildSel}>
              <SelectTrigger>
                <SelectValue placeholder="Select boat" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.event_build_id} value={t.event_build_id}>
                    {t.team_code} — {t.boat_code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Task</Label>
          <Select value={taskType} onValueChange={(v) => setTaskType(v as ScheduleTaskType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCHEDULE_TASK_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="slot-when">When</Label>
          <Input
            id="slot-when"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Leave blank to keep it as an unscheduled backlog item.
          </p>
        </div>
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        {isEdit ? (
          <Button
            variant="ghost"
            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
            onClick={remove}
            disabled={actions.isSaving}
          >
            Delete
          </Button>
        ) : (
          <span />
        )}
        <Button onClick={save} disabled={!canSave}>
          {isEdit ? "Save" : "Schedule"}
        </Button>
      </DialogFooter>
    </>
  );
}
